import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';

/** Long enough to read the end message before the page leaves. */
const REDIRECT_DELAY_SECONDS = 8;

/** Only plain http(s) targets; the API filters too, but `javascript:` must never reach location.assign. */
function parseWebUrl(url: string): URL | null {
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed : null;
    } catch {
        return null;
    }
}

interface RedirectNoticeProps {
    url: string;
    className?: string;
}

/** The author's end-of-test redirect: a short countdown the respondent can skip or cancel. */
const RedirectNotice: React.FC<RedirectNoticeProps> = ({ url, className }) => {
    const { t } = useTranslation();
    const target = parseWebUrl(url);
    const [secondsLeft, setSecondsLeft] = useState(REDIRECT_DELAY_SECONDS);
    const [cancelled, setCancelled] = useState(false);

    useEffect(() => {
        if (!target || cancelled) return;
        if (secondsLeft === 0) {
            window.location.assign(target.href);
            return;
        }
        const tick = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => clearTimeout(tick);
    }, [target, cancelled, secondsLeft]);

    if (!target || cancelled) return null;

    return (
        <aside
            className={`flex flex-col gap-3 rounded-xl border border-border bg-card px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8 ${className ?? ''}`}
        >
            <p className="text-sm leading-relaxed text-muted-foreground">
                {t('result_redirect_countdown', { host: target.hostname, seconds: secondsLeft })}
            </p>
            <div className="flex gap-2">
                <Button className="h-11 flex-1 gap-2 sm:h-10 sm:flex-none" onClick={() => window.location.assign(target.href)}>
                    {t('result_redirect_now')}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                </Button>
                <Button variant="outline" className="h-11 flex-1 sm:h-10 sm:flex-none" onClick={() => setCancelled(true)}>
                    {t('result_redirect_stay')}
                </Button>
            </div>
        </aside>
    );
};

export default RedirectNotice;
